// Implémentation de Human : corps morphable MakeHuman + squelette 163 os + proxies + morphs temps réel.
import * as THREE from 'three';
import type {
  CatalogEntry, CreateHumanOptions, Human, HumanParams, MaterialKind, MaterialProvider, ProxySlot,
} from './types';
import { nearestBodyMap, pushClothOut, accumulateSkinDepth, accumulateSkinDepthSurface, featherDepth, type ClothBinding } from './collide';
import { fetchBin, fetchJson, resolveUrl } from './fetcher';
import { viewOf, type BaseMeta, type IndexArray, type MaterialAsset, type ModifiersFile, type SkeletonDef } from './format';
import { cloneParams, computeTargetWeights, defaultParams } from './modifiers';
import { addTarget, TargetStore, type SparseTarget } from './targets';
import { HumanSkeleton } from './skeleton';
import { computeNormals, expand3, logicalTris, MorphNormalBuilder, setBounds, vertexTriangleAdjacency } from './geometry';
import { defaultMaterial, disposeDefaultMaterial, materialRequest } from './materials';
import { loadProxyAsset, ProxyInstance } from './proxy';
import { drapeClothSteps, drapeSweepSteps, lastDrapeStats, drapeTuning, skinPositions, PointGrid, type DrapeSeat } from './drape';

/** Options supplémentaires (facultatives) acceptées par createHuman. */
export interface CreateHumanExtraOptions {
  /** Morphs temps réel à charger : true (défaut) = faceunits + visèmes, false = aucun, ou liste de noms. */
  morphs?: boolean | string[];
  /** Cibles supplémentaires (chemins complets de targets/index.json, ex. "expression/units/caucasian/mouth-corner-puller")
   *  chargées comme morphs temps réel ; nom du morph = chemin sans le premier segment. */
  extraMorphs?: string[];
  /** Deltas de normales pour les morphs du corps (défaut true ; ~2x mémoire GPU des morphs). */
  morphNormals?: boolean;
}

export interface CatalogFile {
  version: number;
  defaults: Record<string, string>;
  entries: CatalogEntry[];
}

const FALLBACK_DEFAULTS: Record<string, string> = {
  skin: 'skins/young_caucasian_female', eyeMaterial: 'eyes/materials/brown', eyes: 'eyes/high-poly',
  eyebrows: 'eyebrows/eyebrow001', eyelashes: 'eyelashes/eyelashes01', teeth: 'teeth/teeth_base', tongue: 'tongue/tongue01',
  hair: 'hair/bob02',
};
const ANATOMY_SLOTS = ['eyes', 'eyebrows', 'eyelashes', 'teeth', 'tongue'] as const;

export async function loadCatalog(baseUrl: string): Promise<CatalogFile> {
  return fetchJson<CatalogFile>(resolveUrl(baseUrl, 'catalog.json'));
}

export async function loadModifiers(baseUrl: string): Promise<ModifiersFile> {
  return fetchJson<ModifiersFile>(resolveUrl(baseUrl, 'modifiers.json'));
}

function slotKind(slot: string): MaterialKind {
  if (slot.startsWith('clothes:')) return 'clothes';
  return slot as MaterialKind;
}

const morphShortName = (n: string) => n.slice(n.indexOf('/') + 1);

export class HumanImpl implements Human {
  /** Correction anti-transpercement vêtements/peau (désactivable pour comparer en debug). */
  static collision = true;
  readonly root = new THREE.Group();
  skeleton!: THREE.Skeleton;
  bones!: ReadonlyMap<string, THREE.Bone>;
  body!: THREE.SkinnedMesh;
  readonly parts = new Map<string, THREE.SkinnedMesh>();
  morphNames: string[] = [];
  params: HumanParams;

  private readonly baseUrl: string;
  private readonly provider: MaterialProvider | null;
  private readonly opts: CreateHumanOptions & CreateHumanExtraOptions;
  private meta!: BaseMeta;
  private mods!: ModifiersFile;
  private targets!: TargetStore;
  private skel!: HumanSkeleton;
  private defaults: Record<string, string> = { ...FALLBACK_DEFAULTS };

  // corps
  private basePos!: Float32Array;       // positions d'origine (m)
  private pos!: Float32Array;           // positions morphées (+ décalage au sol)
  private baseNrm!: Float32Array;       // normales par sommet de base
  private r2b!: Uint16Array;
  private bodyFullIndex!: IndexArray;
  private quads!: Uint16Array;
  private bodyTris!: Uint32Array;       // triangles du corps en indices de base
  private bodyVerts!: Uint32Array;      // sommets de base utilisés par le corps
  private rPos!: Float32Array;
  private rNrm!: Float32Array;
  private groundOffset = 0;
  // Anti-transpercement : sommet de base → sommet du corps le plus proche, et enfoncement de peau par sommet.
  private toBody!: Int32Array;
  private skinDepth!: Float32Array;

  // morphs temps réel
  private morphTargets: { name: string; target: SparseTarget }[] = [];
  private readonly influences = new Map<string, number>();
  private scratch!: Float32Array;
  private mark!: Uint8Array;

  // proxies et matériaux
  private readonly proxies = new Map<string, ProxyInstance>();
  private readonly slotTokens = new Map<string, number>();
  private readonly ownedMaterials = new Set<THREE.Material>();
  private skinId = '';
  private eyeMaterialId = '';
  private paramsVersion = 0;
  private disposed = false;

  constructor(opts: CreateHumanOptions & CreateHumanExtraOptions) {
    this.opts = opts;
    this.baseUrl = opts.baseUrl.endsWith('/') ? opts.baseUrl : opts.baseUrl + '/';
    this.provider = opts.materialProvider ?? null;
    this.params = cloneParams(opts.params ?? defaultParams());
    this.root.name = 'Human';
  }

  // ------------------------------------------------------------------------------------------
  async init() {
    const url = (r: string) => resolveUrl(this.baseUrl, r);
    const [meta, skelDef, mods, targets, catalog] = await Promise.all([
      fetchJson<BaseMeta>(url('base/base.json')),
      fetchJson<SkeletonDef>(url('base/skeleton.json')),
      fetchJson<ModifiersFile>(url('modifiers.json')),
      TargetStore.load(this.baseUrl),
      fetchJson<CatalogFile>(url('catalog.json')).catch(() => null),
    ]);
    this.meta = meta;
    this.mods = mods;
    this.targets = targets;
    if (catalog?.defaults) Object.assign(this.defaults, catalog.defaults);

    const weights = computeTargetWeights(this.params, mods);
    const morphList = this.runtimeMorphList();
    const [baseBuf, wBuf] = await Promise.all([
      fetchBin(url('base/' + meta.file)),
      fetchBin(url('base/' + meta.weights.file)),
      targets.ensure([...weights.keys(), ...morphList]),
    ]);

    // --- données de base ---
    const L = meta.layout;
    const nb = meta.vertexCount;
    this.basePos = viewOf<Float32Array>(baseBuf, L, 'positions');
    this.pos = new Float32Array(nb * 3);
    this.baseNrm = new Float32Array(nb * 3);
    this.r2b = viewOf<Uint16Array>(baseBuf, L, 'renderToBase');
    this.bodyFullIndex = viewOf<IndexArray>(baseBuf, L, 'index');
    this.quads = viewOf<Uint16Array>(baseBuf, L, 'quads');
    this.bodyTris = logicalTris(this.bodyFullIndex, this.r2b);
    const used = new Uint8Array(nb);
    for (let i = 0; i < this.r2b.length; i++) used[this.r2b[i]] = 1;
    const bv: number[] = [];
    used.forEach((u, i) => u && bv.push(i));
    this.bodyVerts = Uint32Array.from(bv);
    this.scratch = new Float32Array(nb * 3);
    this.mark = new Uint8Array(nb);
    const nr = meta.renderVertexCount;
    this.rPos = new Float32Array(nr * 3);
    this.rNrm = new Float32Array(nr * 3);

    // --- squelette ---
    this.skel = new HumanSkeleton(skelDef);
    this.skeleton = this.skel.skeleton;
    this.bones = this.skel.byName;
    for (const r of this.skel.roots) this.root.add(r);

    this.morphBase(weights);
    this.skel.fit(this.pos);

    // --- corps ---
    const si = viewOf<Uint8Array>(wBuf, meta.weights.layout, 'skinIndex');
    const sw = viewOf<Uint16Array>(wBuf, meta.weights.layout, 'skinWeight');
    const skinIndex = new Uint16Array(nr * 4), skinWeight = new Float32Array(nr * 4);
    for (let r = 0; r < nr; r++) {
      const b = this.r2b[r] * 4;
      for (let k = 0; k < 4; k++) {
        skinIndex[r * 4 + k] = si[b + k];
        skinWeight[r * 4 + k] = sw[b + k] / 65535;
      }
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(this.rPos, 3));
    g.setAttribute('normal', new THREE.BufferAttribute(this.rNrm, 3));
    g.setAttribute('uv', new THREE.BufferAttribute(viewOf<Float32Array>(baseBuf, L, 'uv'), 2));
    g.setAttribute('skinIndex', new THREE.BufferAttribute(skinIndex, 4));
    g.setAttribute('skinWeight', new THREE.BufferAttribute(skinWeight, 4));
    g.setIndex(new THREE.BufferAttribute(this.bodyFullIndex.slice(), 1));
    this.updateBodyGeometry(g);
    this.morphTargets = morphList.map((n) => ({ name: morphShortName(n), target: targets.peek(n)! })).filter((m) => m.target);
    this.buildBodyMorphs(g);

    const skinId = this.opts.skin || this.defaults.skin;
    const skinMat = await this.loadMaterialAsset('skin', skinId).catch((e) => {
      console.warn('human: peau introuvable', skinId, e);
      return this.makeMaterial('skin', skinId, null, '');
    });
    this.skinId = skinId;
    this.body = new THREE.SkinnedMesh(g, skinMat);
    this.body.name = 'body';
    this.body.frustumCulled = false;
    this.body.boundingSphere = g.boundingSphere!.clone();
    this.body.castShadow = true;
    this.body.receiveShadow = true;
    this.body.bind(this.skeleton, new THREE.Matrix4());
    this.root.add(this.body);

    // --- proxies ---
    this.eyeMaterialId = this.opts.eyeMaterial || this.defaults.eyeMaterial;
    const wanted: Record<string, string> = {};
    if (this.opts.proxies) {
      for (const s of ANATOMY_SLOTS) if (this.defaults[s]) wanted[s] = this.defaults[s];
      for (const [k, v] of Object.entries(this.opts.proxies)) {
        if (v) wanted[k] = v;
        else delete wanted[k];
      }
    } else {
      for (const s of [...ANATOMY_SLOTS, 'hair']) if (this.defaults[s]) wanted[s] = this.defaults[s];
    }
    // Chargement initial : la peau sous les vêtements n'est résolue qu'une fois, la tenue complète en place.
    this.loadingProxies = true;
    await Promise.all(Object.entries(wanted).map(([slot, id]) =>
      this.setProxy(slot as ProxySlot, id).catch((e) => console.warn(`human: proxy ${slot}=${id} ignoré`, e))));
    this.loadingProxies = false;
    this.resolveSkin();
    this.morphNames = this.collectMorphNames();
  }

  private runtimeMorphList(): string[] {
    const m = this.opts.morphs ?? true;
    const all = this.targets.index.runtimeMorphs;
    const extra = (this.opts.extraMorphs ?? []).filter((n) => n in this.targets.index.targets && !all.includes(n));
    if (m === false) return extra;
    if (m === true) return [...all, ...extra];
    const set = new Set(m);
    return [...all.filter((n) => set.has(n) || set.has(morphShortName(n))), ...extra];
  }

  // ------------------------------------------------------------------------------------------
  // Morphing CPU : base + Σ w·cible, puis pieds posés sur y = 0.
  private morphBase(weights: Map<string, number>) {
    const pos = this.pos;
    pos.set(this.basePos);
    for (const [name, w] of weights) {
      const t = this.targets.peek(name);
      if (t) addTarget(pos, t, w);
    }
    let minY = Infinity;
    for (let i = 0; i < this.bodyVerts.length; i++) minY = Math.min(minY, pos[this.bodyVerts[i] * 3 + 1]);
    this.groundOffset = -minY;
    for (let i = 1; i < pos.length; i += 3) pos[i] -= minY;
  }

  private updateBodyGeometry(g: THREE.BufferGeometry) {
    computeNormals(this.pos, this.bodyTris, this.baseNrm);
    expand3(this.pos, this.r2b, this.rPos);
    // Réapplique le dernier enfoncement connu (évite un "pop" de la peau pendant un glissement de curseur).
    if (this.skinDepth) {
      const r2b = this.r2b, nrm = this.baseNrm, out = this.rPos, depth = this.skinDepth;
      for (let r = 0, n = r2b.length; r < n; r++) {
        const b = r2b[r], d = depth[b];
        if (d > 0) { out[r * 3] -= d * nrm[b * 3]; out[r * 3 + 1] -= d * nrm[b * 3 + 1]; out[r * 3 + 2] -= d * nrm[b * 3 + 2]; }
      }
    }
    expand3(this.baseNrm, this.r2b, this.rNrm);
    g.attributes.position.needsUpdate = true;
    g.attributes.normal.needsUpdate = true;
    setBounds(g, this.rPos);
    if (this.body) this.body.boundingSphere = g.boundingSphere!.clone();
  }

  private buildBodyMorphs(g: THREE.BufferGeometry) {
    const nr = this.r2b.length;
    const withNormals = this.opts.morphNormals ?? true;
    const nb = this.meta.vertexCount;
    const nb3 = withNormals ? new MorphNormalBuilder(this.pos, this.baseNrm, this.bodyTris, vertexTriangleAdjacency(this.bodyTris, nb)) : null;
    const nDelta = withNormals ? new Float32Array(nb * 3) : null;
    const used = new Uint8Array(nb);
    for (let i = 0; i < this.bodyVerts.length; i++) used[this.bodyVerts[i]] = 1;
    const posAttrs: THREE.BufferAttribute[] = [], nrmAttrs: THREE.BufferAttribute[] = [];
    const { scratch } = this;
    for (const { name, target } of this.morphTargets) {
      const { idx, d, scale } = target;
      let hits = 0;
      for (let i = 0; i < idx.length; i++) {
        const o = idx[i] * 3;
        scratch[o] = d[i * 3] * scale; scratch[o + 1] = d[i * 3 + 1] * scale; scratch[o + 2] = d[i * 3 + 2] * scale;
        hits += used[idx[i]];
      }
      if (hits > 0) {
        const pa = new Float32Array(nr * 3);
        expand3(scratch, this.r2b, pa);
        const attr = new THREE.BufferAttribute(pa, 3);
        attr.name = name;
        posAttrs.push(attr);
        if (nb3 && nDelta) {
          nb3.build(scratch, idx, nDelta);
          const na = new Float32Array(nr * 3);
          expand3(nDelta, this.r2b, na);
          for (let i = 0; i < nb3.touchedCount; i++) {
            const o = nb3.touched[i] * 3;
            nDelta[o] = nDelta[o + 1] = nDelta[o + 2] = 0;
          }
          const nattr = new THREE.BufferAttribute(na, 3);
          nattr.name = name;
          nrmAttrs.push(nattr);
        }
      }
      for (let i = 0; i < idx.length; i++) {
        const o = idx[i] * 3;
        scratch[o] = scratch[o + 1] = scratch[o + 2] = 0;
      }
    }
    if (posAttrs.length) {
      g.morphAttributes.position = posAttrs;
      if (nrmAttrs.length) g.morphAttributes.normal = nrmAttrs;
      g.morphTargetsRelative = true;
    }
  }

  private collectMorphNames(): string[] {
    const names = new Set<string>();
    for (const m of this.morphTargets) names.add(m.name);
    return [...names];
  }

  private applyInfluences(mesh: THREE.Mesh) {
    const dict = mesh.morphTargetDictionary, inf = mesh.morphTargetInfluences;
    if (!dict || !inf) return;
    for (const [name, w] of this.influences) {
      const i = dict[name];
      if (i !== undefined) inf[i] = w;
    }
  }

  // ------------------------------------------------------------------------------------------
  // Matériaux
  private makeMaterial(kind: MaterialKind, assetId: string, def: MaterialAsset['material'] | null, assetUrl: string) {
    const req = materialRequest(kind, assetId, def, assetUrl || resolveUrl(this.baseUrl, assetId + '/'));
    if (this.provider) return this.provider(req);
    const m = defaultMaterial(req);
    this.ownedMaterials.add(m);
    return m;
  }

  private async loadMaterialAsset(kind: MaterialKind, id: string): Promise<THREE.Material> {
    const assetUrl = resolveUrl(this.baseUrl, id + '/');
    const a = await fetchJson<MaterialAsset>(assetUrl + 'asset.json');
    return this.makeMaterial(kind, id, a.material, assetUrl);
  }

  private releaseMaterial(m: THREE.Material | THREE.Material[]) {
    for (const mat of Array.isArray(m) ? m : [m]) {
      if (this.ownedMaterials.has(mat)) {
        this.ownedMaterials.delete(mat);
        disposeDefaultMaterial(mat);
      }
    }
  }

  // ------------------------------------------------------------------------------------------
  // API publique
  setMorph(name: string, weight: number): void {
    const w = Number.isFinite(weight) ? weight : 0;
    this.influences.set(name, w);
    const set = (mesh: THREE.Mesh) => {
      const i = mesh.morphTargetDictionary?.[name];
      if (i !== undefined) mesh.morphTargetInfluences![i] = w;
    };
    set(this.body);
    for (const p of this.proxies.values()) set(p.mesh);
  }

  getMorph(name: string): number {
    return this.influences.get(name) ?? 0;
  }

  async setParams(params: HumanParams): Promise<void> {
    this.cancelDrape();
    const version = ++this.paramsVersion;
    const p = cloneParams(params);
    const weights = computeTargetWeights(p, this.mods);
    await this.targets.ensure(weights.keys());
    if (version !== this.paramsVersion || this.disposed) return; // un appel plus récent a pris la main
    this.cancelDrape();
    this.params = p;
    this.morphBase(weights);
    this.updateBodyGeometry(this.body.geometry);
    this.skel.fit(this.pos);
    for (const px of this.proxies.values()) px.fit(this.pos, this.collideFor(px));
    this.updateMasks();
    this.scheduleSkin();
  }

  /** Sommets du corps encore rendus (ceux masqués par les delete_verts n'ont pas besoin d'être corrigés). */
  private visibleBodyVerts(): Uint32Array {
    const g = this.body.geometry;
    const idx = g.index!.array as IndexArray;
    const end = Math.min(idx.length, g.drawRange.count === Infinity ? idx.length : g.drawRange.start + g.drawRange.count);
    const nb = this.meta.vertexCount;
    const used = new Uint8Array(nb);
    let n = 0;
    for (let i = g.drawRange.start; i < end; i++) {
      const b = this.r2b[idx[i]];
      if (!used[b]) { used[b] = 1; n++; }
    }
    const out = new Uint32Array(n);
    for (let v = 0, k = 0; v < nb; v++) if (used[v]) out[k++] = v;
    return out;
  }

  /**
   * Pendant un glissement de curseur (appels rapprochés), la correction complète est différée à la pause
   * suivante (~150 ms) ; un appel isolé est corrigé immédiatement.
   */
  private skinTimer = 0;
  private loadingProxies = false;
  private lastSkinCall = 0;
  private scheduleSkin() {
    const now = performance.now();
    const rapid = now - this.lastSkinCall < 120;
    this.lastSkinCall = now;
    clearTimeout(this.skinTimer);
    if (!rapid) { this.resolveSkin(); return; }
    // Repli rapide : positions de rendu sans enfoncement recalculé (garde l'ancien), correction à la pause.
    this.skinTimer = window.setTimeout(() => { if (!this.disposed) this.resolveSkin(); }, 150);
  }

  /** Correction "vêtement au-dessus de la peau" pour les vêtements uniquement. */
  private collideFor(px: ProxyInstance) {
    if (!HumanImpl.collision || !px.slot.startsWith('clothes:')) return undefined;
    this.ensureToBody();
    return (b: ClothBinding) => pushClothOut(b, this.pos, this.baseNrm, this.toBody);
  }

  private ensureToBody() {
    if (!this.toBody) this.toBody = nearestBodyMap(this.pos, this.bodyVerts, this.meta.vertexCount);
  }

  /**
   * Enfonce légèrement la peau sous les vêtements portés (au repos, pour la forme courante) puis réécrit
   * les positions de rendu du corps. Sans vêtement : corps intact.
   */
  private resolveSkin() {
    const nb = this.meta.vertexCount;
    if (!this.skinDepth) this.skinDepth = new Float32Array(nb);
    const depth = this.skinDepth;
    depth.fill(0);
    let any = false;
    for (const px of this.proxies.values()) {
      if (!HumanImpl.collision || !px.slot.startsWith('clothes:')) continue;
      this.ensureToBody();
      accumulateSkinDepth(px, this.pos, this.baseNrm, this.toBody, depth);
      accumulateSkinDepthSurface(px, this.pos, this.baseNrm, this.visibleBodyVerts(), depth);
      any = true;
    }
    if (any) featherDepth(depth, this.bodyTris);
    // Positions de rendu = base (+ enfoncement le long de la normale).
    expand3(this.pos, this.r2b, this.rPos);
    if (any) {
      const r2b = this.r2b, nrm = this.baseNrm, out = this.rPos;
      for (let r = 0, n = r2b.length; r < n; r++) {
        const b = r2b[r], d = depth[b];
        if (d <= 0) continue;
        out[r * 3] -= d * nrm[b * 3]; out[r * 3 + 1] -= d * nrm[b * 3 + 1]; out[r * 3 + 2] -= d * nrm[b * 3 + 2];
      }
    }
    this.body.geometry.attributes.position.needsUpdate = true;
    this.updateAnchors();
  }

  /** Écart à la peau visé par la collision au rendu (m) : au moins `anchorGap`, au plus `anchorGapMax`. */
  static anchorGap = 0.002;
  static anchorGapMax = 0.006;
  /** Distance au repos au-delà de laquelle un sommet de vêtement n'est pas ancré (m). */
  static anchorReach = 0.08;

  /**
   * Collision tissu / peau au rendu, dans toutes les poses (marche, assise, vent, drapé) : chaque sommet de vêtement
   * reçoit le point de peau VISIBLE le plus proche au repos (position enfoncée comprise, normale, os et poids). Le
   * shader des vêtements le déforme avec le squelette (parties molles comprises, ce sont des os) et repousse le tissu
   * qui passerait sous cette peau. La peau masquée (hiddenSkin) n'est pas dessinée : rien à y protéger. Les bras ne
   * portent que les vêtements qui les suivent (manches) : sinon un bustier s'accrocherait à l'aisselle.
   */
  private updateAnchors() {
    const clothes = [...this.proxies.values()].filter((p) => p.slot.startsWith('clothes:'));
    if (!clothes.length) return;
    const nb = this.meta.vertexCount, P = this.pos, N = this.baseNrm, depth = this.skinDepth;
    const g = this.body.geometry;
    const SI = g.attributes.skinIndex.array as ArrayLike<number>, SW = g.attributes.skinWeight.array as ArrayLike<number>;
    const b2r = new Int32Array(nb).fill(-1);
    for (let r = 0; r < this.r2b.length; r++) if (b2r[this.r2b[r]] < 0) b2r[this.r2b[r]] = r;
    const armRe = /^(shoulder|upperarm|lowerarm|wrist|finger|metacarpal)/;
    const armBone = Uint8Array.from(this.skeleton.bones, (b) => (armRe.test(b.name) ? 1 : 0));
    const domBone = (si: ArrayLike<number>, sw: ArrayLike<number>, r: number) => {
      let k = 0;
      for (let j = 1; j < 4; j++) if (sw[r * 4 + j] > sw[r * 4 + k]) k = j;
      return si[r * 4 + k];
    };
    // Surface de repos (enfoncement compris) des sommets visibles : tous, et sans les bras.
    const vis = this.visibleBodyVerts();
    const S = new Float32Array(nb * 3);
    const all: number[] = [], noArm: number[] = [];
    for (let i = 0; i < vis.length; i++) {
      const b = vis[i], r = b2r[b];
      if (r < 0) continue;
      const d = depth ? depth[b] : 0;
      for (let c = 0; c < 3; c++) S[b * 3 + c] = P[b * 3 + c] - d * N[b * 3 + c];
      all.push(b);
      if (!armBone[domBone(SI, SW, r)]) noArm.push(b);
    }
    const gridOf = (list: number[]) => {
      const sp = new Float32Array(list.length * 3);
      list.forEach((b, i) => { sp[i * 3] = S[b * 3]; sp[i * 3 + 1] = S[b * 3 + 1]; sp[i * 3 + 2] = S[b * 3 + 2]; });
      return { list, grid: new PointGrid(sp, list.length, 0.03) };
    };
    const gAll = gridOf(all), gBody = gridOf(noArm);
    const reach = HumanImpl.anchorReach, gap0 = HumanImpl.anchorGap, gap1 = HumanImpl.anchorGapMax;
    for (const px of clothes) {
      const nv = px.nVerts, C = px.pos, r2v = px.r2v, nr = r2v.length;
      const firstR = new Int32Array(nv).fill(-1);
      for (let r = 0; r < nr; r++) if (firstR[r2v[r]] < 0) firstR[r2v[r]] = r;
      const anchor = new Int32Array(nv).fill(-1), gapOf = new Float32Array(nv);
      for (let v = 0; v < nv; v++) {
        const r = firstR[v];
        if (r < 0) continue;
        const G = armBone[domBone(px.skinIndex, px.skinWeight, r)] ? gAll : gBody;
        const x = C[v * 3], y = C[v * 3 + 1], z = C[v * 3 + 2];
        const i = G.grid.nearest(x, y, z, reach);
        if (i < 0) continue;
        const b = G.list[i];
        const sd = (x - S[b * 3]) * N[b * 3] + (y - S[b * 3 + 1]) * N[b * 3 + 1] + (z - S[b * 3 + 2]) * N[b * 3 + 2];
        if (sd < -0.02) continue; // tissu déjà dans la peau au repos : ancrage douteux (autre membre)
        anchor[v] = b;
        gapOf[v] = Math.min(gap1, Math.max(gap0, sd));
      }
      const aPos = new Float32Array(nr * 4), aNrm = new Float32Array(nr * 3), aIdx = new Float32Array(nr * 4), aWgt = new Float32Array(nr * 4);
      for (let r = 0; r < nr; r++) {
        const v = r2v[r], b = anchor[v];
        if (b < 0) continue;
        const rb = b2r[b];
        aPos[r * 4] = S[b * 3]; aPos[r * 4 + 1] = S[b * 3 + 1]; aPos[r * 4 + 2] = S[b * 3 + 2]; aPos[r * 4 + 3] = gapOf[v];
        aNrm[r * 3] = N[b * 3]; aNrm[r * 3 + 1] = N[b * 3 + 1]; aNrm[r * 3 + 2] = N[b * 3 + 2];
        for (let k = 0; k < 4; k++) { aIdx[r * 4 + k] = SI[rb * 4 + k]; aWgt[r * 4 + k] = SW[rb * 4 + k]; }
      }
      px.setAnchors(aPos, aNrm, aIdx, aWgt);
    }
  }

  // ------------------------------------------------------------------------------------------
  // Drapé des jupes (pose assise / au sol) : calculé une fois par pose, fondu enchaîné à l'affichage.
  private drapeGoal = 0;
  private drapeW = 0;

  private drapeJob: Generator<void, void> | null = null;

  /**
   * Drape les jupes pour la pose courante du squelette. Le calcul est étalé sur les images suivantes
   * (updateDrape) puis fondu. Renvoie le nombre de vêtements concernés.
   */
  drape(seat: DrapeSeat | null, opts: { sweep?: boolean } = {}): number {
    const clothes = [...this.proxies.values()].filter((p) => p.skirtFree);
    this.drapeJob = null;
    this.drapePending = null;
    this.drapeGoal = 0;
    if (!clothes.length) return 0;
    // Matrices de skinning dans le repère du personnage (siège et sol y sont exprimés).
    this.root.updateMatrixWorld(true);
    const bones = this.skeleton.bones, inv = this.skeleton.boneInverses;
    const rootInv = new THREE.Matrix4().copy(this.root.matrixWorld).invert();
    const mats = new Float32Array(bones.length * 16);
    const m = new THREE.Matrix4();
    for (let i = 0; i < bones.length; i++) m.multiplyMatrices(rootInv, bones[i].matrixWorld).multiply(inv[i]).toArray(mats, i * 16);
    const g = this.body.geometry;
    const count = g.attributes.position.count;
    const bp = new Float32Array(count * 3), bn = new Float32Array(count * 3);
    skinPositions({
      pos: g.attributes.position.array, normal: g.attributes.normal.array,
      skinIndex: g.attributes.skinIndex.array, skinWeight: g.attributes.skinWeight.array, count,
    }, mats, bp, bn);
    const self = this;
    // Choix de la méthode, par vêtement :
    //  - assise sur un siège, jupe COURTE (ourlet au-dessus des genoux) : balayage — le squelette passe de la pose de
    //    repos à la pose voulue, les cuisses poussent le panneau avant sur les genoux (le skinning, lui, le tasse dans
    //    le pli de l'aine) ;
    //  - sinon (jupe longue : genoux et tibias la perceraient pendant le balayage ; au sol, allongée) : départ par
    //    transfert de surface puis simulation.
    const knee = this.skeleton.bones.findIndex((b) => b.name === 'lowerleg01.L');
    const kneeY = knee >= 0 ? new THREE.Matrix4().copy(this.skeleton.boneInverses[knee]).invert().elements[13] : 0.48;
    /** Mini-jupe : ourlet au repos nettement au-dessus du genou. */
    const isMini = (px: ProxyInstance) => {
      const free = px.skirtFree!, P = px.pos;
      let low = Infinity;
      for (let v = 0; v < px.nVerts; v++) if (free[v]) low = Math.min(low, P[v * 3 + 1]);
      return low > kneeY + drapeTuning.sweepAboveKnee;
    };
    const useSweep = (px: ProxyInstance) => {
      if (opts.sweep === false) return false;
      if (drapeTuning.sweep) return true;
      return !!seat && drapeTuning.sweepShortSeated && isMini(px);
    };
    const frames = clothes.some(useSweep) ? this.sweepFrames(drapeTuning.substeps, rootInv) : null;
    // Os qui porte la jupe pendant le balayage : le bassin.
    const carry = this.skeleton.bones.findIndex((b) => b.name === 'root');
    const anchor = this.anchorMask();
    const bodySrc = {
      pos: g.attributes.position.array, normal: g.attributes.normal.array,
      skinIndex: g.attributes.skinIndex.array, skinWeight: g.attributes.skinWeight.array, count,
    };
    this.drapeJob = (function* () {
      const results: Array<[ProxyInstance, Float32Array | null]> = [];
      for (const px of clothes) {
        const cloth = {
          objPos: px.pos, nVerts: px.nVerts, tris: px.tris, r2v: px.r2v,
          skinIndex: px.skinIndex, skinWeight: px.skinWeight, free: px.skirtFree!,
        };
        const simple = { pos: bp, nrm: bn, bindPos: g.attributes.position.array, bindNrm: g.attributes.normal.array, count, anchor };
        if (seat && opts.sweep !== false && drapeTuning.rawSeatedLong && !isMini(px)) {
          lastDrapeStats.method = 'none'; lastDrapeStats.sweepRejected = false;
          results.push([px, null]);
          continue;
        }
        const swept = !!frames && useSweep(px);
        let out = swept ? yield* drapeSweepSteps(cloth, frames!, { src: bodySrc }, seat, carry) : undefined;
        const sweepMs = swept ? lastDrapeStats.ms : 0;
        lastDrapeStats.sweepRejected = swept && !out;
        // Balayage rejeté (ou non choisi) : drapé par transfert de surface.
        if (!out) {
          if (swept) yield;
          out = yield* drapeClothSteps(cloth, mats, simple, seat);
          lastDrapeStats.ms += sweepMs;
        }
        lastDrapeStats.method = swept && !lastDrapeStats.sweepRejected && out ? 'sweep' : out ? 'transfer' : 'none';
        results.push([px, out]);
        yield;
      }
      // Tout est prêt : appliqué dès que l'ancien drapé (s'il y en a un) a fini de s'effacer.
      self.drapePending = results;
    })();
    return clothes.length;
  }

  /**
   * Matrices de skinning (repère personnage) le long d'un balayage de `K` étapes, de la pose de repos (liaison) à la
   * pose courante du squelette : interpolation par os de la transformation LOCALE (position, rotation en slerp,
   * échelle), recomposée le long de la hiérarchie — les membres tournent autour de leurs articulations, sans se
   * déformer. La dernière étape reproduit exactement la pose courante.
   */
  private sweepFrames(K: number, rootInv: THREE.Matrix4): Float32Array[] {
    K = Math.max(1, Math.round(K));
    const bones = this.skeleton.bones, inv = this.skeleton.boneInverses, nb = bones.length;
    const index = new Map<THREE.Object3D, number>(bones.map((b, i) => [b, i]));
    const parent = bones.map((b) => (b.parent && index.has(b.parent) ? index.get(b.parent)! : -1));
    const restW = inv.map((m) => m.clone().invert());
    const poseW = bones.map((b) => new THREE.Matrix4().multiplyMatrices(rootInv, b.matrixWorld));
    const local = (W: THREE.Matrix4[]) => bones.map((_, i) => {
      const L = parent[i] < 0 ? W[i].clone() : W[parent[i]].clone().invert().multiply(W[i]);
      const p = new THREE.Vector3(), q = new THREE.Quaternion(), sc = new THREE.Vector3();
      L.decompose(p, q, sc);
      return { p, q, s: sc };
    });
    const R = local(restW), P = local(poseW);
    const order: number[] = [];
    const seen = new Uint8Array(nb);
    const visit = (i: number) => { if (seen[i]) return; if (parent[i] >= 0) visit(parent[i]); seen[i] = 1; order.push(i); };
    for (let i = 0; i < nb; i++) visit(i);
    const W = bones.map(() => new THREE.Matrix4());
    const L = new THREE.Matrix4(), M = new THREE.Matrix4();
    const p = new THREE.Vector3(), q = new THREE.Quaternion(), sc = new THREE.Vector3();
    const frames: Float32Array[] = [];
    for (let k = 0; k <= K; k++) {
      const t = k / K;
      const f = new Float32Array(nb * 16);
      for (const i of order) {
        p.lerpVectors(R[i].p, P[i].p, t);
        q.slerpQuaternions(R[i].q, P[i].q, t);
        sc.lerpVectors(R[i].s, P[i].s, t);
        L.compose(p, q, sc);
        if (parent[i] < 0) W[i].copy(L); else W[i].multiplyMatrices(W[parent[i]], L);
        M.multiplyMatrices(W[i], inv[i]).toArray(f, i * 16);
      }
      frames.push(f);
    }
    return frames;
  }

  private anchorCache: { geo: THREE.BufferGeometry | null; mask: Uint8Array | null } = { geo: null, mask: null };

  /** Sommets du corps pouvant porter le tissu au départ du drapé : tout sauf bras, avant-bras et mains. */
  private anchorMask(): Uint8Array {
    const g = this.body.geometry;
    if (this.anchorCache.geo === g && this.anchorCache.mask) return this.anchorCache.mask;
    const names = this.skeleton.bones.map((b) => b.name);
    const arm = names.map((nm) => /^(shoulder|upperarm|lowerarm|wrist|finger|metacarpal)/.test(nm));
    const si = g.attributes.skinIndex.array, sw = g.attributes.skinWeight.array, count = g.attributes.position.count;
    const mask = new Uint8Array(count);
    for (let r = 0; r < count; r++) {
      let best = 0, bw = -1;
      for (let k = 0; k < 4; k++) if (sw[r * 4 + k] > bw) { bw = sw[r * 4 + k]; best = si[r * 4 + k]; }
      mask[r] = arm[best] ? 0 : 1;
    }
    this.anchorCache = { geo: g, mask };
    return mask;
  }

  /** Relâche le drapé (fondu vers le skinning normal). */
  undrape() { this.drapeGoal = 0; this.drapeJob = null; this.drapePending = null; }

  /** Abandonne un calcul en cours et relâche le drapé affiché (fondu) : silhouette ou vêtement changés. */
  private cancelDrape() { this.drapeJob = null; this.drapePending = null; this.drapeGoal = 0; }

  /** Résultats d'un calcul terminé, en attente de la fin du fondu de l'ancien drapé. */
  private drapePending: Array<[ProxyInstance, Float32Array | null]> | null = null;

  private applyPending() {
    const results = this.drapePending!;
    this.drapePending = null;
    for (const p of this.proxies.values()) p.blendDrape(0);
    let n = 0;
    for (const [px, out] of results) if (this.proxies.get(px.slot) === px) { px.setDrape(out); if (out) n++; }
    this.drapeW = 0;
    this.drapeGoal = n ? 1 : 0;
  }

  /** Calcul étalé (≈6 ms par image) puis fondu du drapé, à appeler chaque image. */
  updateDrape(dt: number) {
    if (this.drapeJob) {
      const t0 = performance.now();
      while (this.drapeJob && performance.now() - t0 < 6) if (this.drapeJob.next().done) this.drapeJob = null;
    }
    if (this.drapePending) { if (this.drapeW === 0) this.applyPending(); else this.drapeGoal = 0; }
    if (this.drapeW === this.drapeGoal) return;
    const step = dt / (this.drapeGoal ? 0.6 : 0.35);
    this.drapeW = this.drapeGoal ? Math.min(1, this.drapeW + step) : Math.max(0, this.drapeW - step);
    const w = this.drapeW * this.drapeW * (3 - 2 * this.drapeW);
    for (const px of this.proxies.values()) if (px.skirtFree) px.blendDrape(w);
    if (this.drapeW === 0 && this.drapeGoal === 0) for (const px of this.proxies.values()) px.setDrape(null);
  }

  /** Calcul de drapé en cours. */
  get draping() { return this.drapeJob !== null || this.drapePending !== null; }

  async setProxy(slot: ProxySlot, assetId: string | null): Promise<void> {
    if (slot.startsWith('clothes')) this.cancelDrape();
    const token = (this.slotTokens.get(slot) ?? 0) + 1;
    this.slotTokens.set(slot, token);
    if (!assetId) {
      this.removeProxy(slot);
      this.updateMasks();
      if (slot.startsWith('clothes:')) this.resolveSkin();
      return;
    }
    const { asset, buf, assetUrl } = await loadProxyAsset(this.baseUrl, assetId);
    if (this.slotTokens.get(slot) !== token || this.disposed) return;
    const kind = slotKind(slot);
    let material: THREE.Material;
    if (kind === 'eyes' && this.eyeMaterialId) {
      material = await this.loadMaterialAsset('eyes', this.eyeMaterialId)
        .catch(() => this.makeMaterial('eyes', assetId, asset.material, assetUrl));
    } else {
      material = this.makeMaterial(kind, assetId, asset.material, assetUrl);
    }
    if (this.slotTokens.get(slot) !== token || this.disposed) {
      this.releaseMaterial(material);
      return;
    }
    const skirt = this.meta.groups['helper-skirt'];
    const px = new ProxyInstance(slot, asset, buf, assetUrl, material, this.skeleton,
      slot.startsWith('clothes:') && skirt ? [skirt.vmin, skirt.vmax] : undefined);
    px.fit(this.pos, this.collideFor(px));
    px.buildMorphs(this.morphTargets, this.scratch, this.mark);
    this.applyInfluences(px.mesh);
    if (slot.startsWith('clothes')) this.cancelDrape();
    this.removeProxy(slot);
    this.proxies.set(slot, px);
    this.parts.set(slot, px.mesh);
    this.root.add(px.mesh);
    this.updateMasks();
    if (slot.startsWith('clothes:') && !this.loadingProxies) this.resolveSkin();
  }

  private removeProxy(slot: string) {
    const old = this.proxies.get(slot);
    if (!old) return;
    this.proxies.delete(slot);
    this.parts.delete(slot);
    this.releaseMaterial(old.mesh.material);
    old.dispose();
  }

  /** Peau masquée sous un vêtement : tissu à moins de `maskTight` (m ; `maskTightSkirt` sous la partie libre d'une
   *  jupe, qui bouge) ; marge de peau gardée aux bords (m). */
  static maskTight = 0.015;
  static maskTightSkirt = 0.008;
  static maskMargin = 0.02;

  /**
   * Sommets de peau réellement cachés par les vêtements. Les delete_verts de MakeHuman ont été tracés pour le corps de
   * base, au repos : appliqués tels quels, ils retirent aussi la peau sous un bustier qui s'écarte de la poitrine
   * (décolleté vide), sous la partie libre d'une jupe (cuisses absentes dès qu'elle se soulève : on voit l'envers du
   * tissu) et au ras des bords. Ici, un sommet des delete_verts n'est masqué que si le tissu ajusté le touche presque
   * (≤ maskTight, ≤ maskTightSkirt sous une jupe, et pas en dessous de la peau), et s'il est à plus de maskMargin de
   * toute peau restée visible : décolleté, emmanchures, taille et ourlet gardent toujours de la peau sous leur bord.
   */
  private hiddenSkin(clothes: ProxyInstance[]): Uint8Array {
    const nb = this.meta.vertexCount, P = this.pos, N = this.baseNrm;
    const cand = new Uint8Array(nb);
    const tight = HumanImpl.maskTight;
    for (const px of clothes) {
      const del = px.deleteVerts;
      if (!del || !del.length) continue;
      const C = px.pos, free = px.skirtFree;
      const grid = new PointGrid(C, px.nVerts, 0.02);
      for (let i = 0; i < del.length; i++) {
        const v = del[i], o = v * 3;
        if (v >= nb || cand[v]) continue;
        const c = grid.nearest(P[o], P[o + 1], P[o + 2], tight);
        if (c < 0) continue;
        if (free && free[c]) {
          // Jupe : seulement là où elle épouse la peau (fesses, hanches), jamais sous un pan qui flotte.
          const d = Math.hypot(C[c * 3] - P[o], C[c * 3 + 1] - P[o + 1], C[c * 3 + 2] - P[o + 2]);
          if (d > HumanImpl.maskTightSkirt) continue;
        }
        const s = (C[c * 3] - P[o]) * N[o] + (C[c * 3 + 1] - P[o + 1]) * N[o + 1] + (C[c * 3 + 2] - P[o + 2]) * N[o + 2];
        if (s < -0.005) continue;
        cand[v] = 1;
      }
    }
    // Marge aux bords : tout candidat proche d'une peau visible reste visible.
    const bv = this.bodyVerts;
    let nv = 0;
    for (let i = 0; i < bv.length; i++) if (!cand[bv[i]]) nv++;
    const vp = new Float32Array(nv * 3);
    for (let i = 0, k = 0; i < bv.length; i++) {
      const v = bv[i];
      if (cand[v]) continue;
      vp[k++] = P[v * 3]; vp[k++] = P[v * 3 + 1]; vp[k++] = P[v * 3 + 2];
    }
    const vg = new PointGrid(vp, nv, 0.02);
    const hidden = new Uint8Array(nb);
    for (let i = 0; i < bv.length; i++) {
      const v = bv[i];
      if (cand[v] && vg.nearest(P[v * 3], P[v * 3 + 1], P[v * 3 + 2], HumanImpl.maskMargin) < 0) hidden[v] = 1;
    }
    return hidden;
  }

  /**
   * Masquage des faces cachées par les vêtements (plugins/3_libraries_clothes_chooser.updateFaceMasks) :
   * du vêtement le plus externe (z_depth élevé) au plus interne, chaque vêtement est masqué par les
   * delete_verts des couches supérieures. Le corps, lui, n'est masqué que là où la peau est vraiment
   * couverte (hiddenSkin).
   */
  private updateMasks() {
    const clothes = [...this.proxies.values()].filter((p) => p.slot.startsWith('clothes:'))
      .sort((a, b) => b.asset.zDepth - a.asset.zDepth);
    const nb = this.meta.vertexCount;
    const visible = new Uint8Array(nb).fill(1);
    let any = false;
    // Écart volontaire à MakeHuman : deux vêtements de même z_depth ne se masquent pas entre eux
    // (MakeHuman départage par uuid, ce qui peut faire disparaître entièrement une jupe sous un pantalon).
    for (let i = 0; i < clothes.length;) {
      let j = i;
      while (j < clothes.length && clothes[j].asset.zDepth === clothes[i].asset.zDepth) j++;
      for (let k = i; k < j; k++) clothes[k].applyMask(any ? visible : null);
      for (let k = i; k < j; k++) {
        const del = clothes[k].deleteVerts;
        if (del && del.length) {
          for (let n = 0; n < del.length; n++) visible[del[n]] = 0;
          any = true;
        }
      }
      i = j;
    }
    const g = this.body.geometry;
    const out = g.index!.array as IndexArray;
    const hidden = any ? this.hiddenSkin(clothes) : null;
    if (!hidden) {
      out.set(this.bodyFullIndex);
      g.setDrawRange(0, this.bodyFullIndex.length);
    } else {
      const q = this.quads, src = this.bodyFullIndex;
      let w = 0;
      for (let f = 0, nq = q.length / 4; f < nq; f++) {
        const o = f * 4;
        if (!(hidden![q[o]] & hidden![q[o + 1]] & hidden![q[o + 2]] & hidden![q[o + 3]])) {
          for (let k = f * 6; k < f * 6 + 6; k++) out[w++] = src[k];
        }
      }
      g.setDrawRange(0, w);
    }
    g.index!.needsUpdate = true;
  }

  async setSkin(assetId: string): Promise<void> {
    const id = assetId || this.defaults.skin;
    const mat = await this.loadMaterialAsset('skin', id);
    if (this.disposed) return this.releaseMaterial(mat);
    this.releaseMaterial(this.body.material);
    this.body.material = mat;
    this.skinId = id;
  }

  async setEyeMaterial(assetId: string): Promise<void> {
    const id = assetId || this.defaults.eyeMaterial;
    this.eyeMaterialId = id;
    const eyes = this.proxies.get('eyes');
    if (!eyes) return;
    const mat = await this.loadMaterialAsset('eyes', id);
    if (this.disposed || this.proxies.get('eyes') !== eyes) return this.releaseMaterial(mat);
    this.releaseMaterial(eyes.mesh.material);
    eyes.mesh.material = mat;
  }

  /** Identifiants actuellement utilisés (peau, matériau d'yeux, proxies par slot). */
  get current() {
    const proxies: Record<string, string> = {};
    for (const [slot, p] of this.proxies) proxies[slot] = p.asset.id;
    return { skin: this.skinId, eyeMaterial: this.eyeMaterialId, proxies };
  }

  /** Décalage vertical appliqué pour poser les pieds sur y = 0 (m). */
  get groundOffsetY() {
    return this.groundOffset;
  }

  /** Positions de base morphées courantes (m, repère root) — lecture seule. */
  get morphedPositions(): Float32Array {
    return this.pos;
  }

  /** Remet le squelette en pose de repos. */
  resetPose() {
    this.skel.resetPose();
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    for (const slot of [...this.proxies.keys()]) this.removeProxy(slot);
    this.releaseMaterial(this.body.material);
    this.body.geometry.dispose();
    this.skeleton.dispose();
    this.root.removeFromParent();
    this.root.clear();
  }
}
